import { Component, computed, inject, OnInit, ViewChild } from '@angular/core';
import {
  combineLatest,
  firstValueFrom,
  Observable,
  skipWhile,
  take,
} from 'rxjs';
import { Room } from '../../../core/models/entities/room';
import { Store } from '@ngrx/store';
import { fromStore } from 'angular/app/core/store';
import { ActivatedRoute, Router } from '@angular/router';
import { bookingFormActions } from 'angular/app/core/store/booking-form';
import { roomsActions } from 'angular/app/core/store/rooms';
import { PublicBookingComponent } from '../../../shared/components/public-booking/public-booking.component';
import { Title } from '@angular/platform-browser';
import { ToastService } from '../../../core/services/toast.service';
import { Booking } from '../../../core/models/entities/booking';
import {
  selectAuthUser,
  selectBookingFormData,
  selectRoomsError,
  selectRoomsLoading,
} from '../../../core/store/reducers';
import { BookableBy } from '../../../core/models/common/bookable-by';
import { BookingPermission } from '../../../core/models/entities/permission';
import { AuthService } from '../../../core/services/auth.service';
import { LogoutBaseComponent } from '../../../shared/components/logout-base/logout-base.component';
import { ModalService } from '../../../core/services/modal.service';
import { RoomsService } from '../../../core/services/rooms.service';
import { TranslocoService } from '@jsverse/transloco';
import { LocalizedUrlService } from '../../../core/services/localized-url.service';

@Component({
  selector: 'app-booking-page',
  template: `
    @if (
      (room$ | async) &&
      !roomLoadingError() &&
      !roomIsInactiveOrPrivateOrNotListed() &&
      !authUserIsNotAllowedToCreatePublicBooking()
    ) {
      @if (room$ | async; as room) {
        <div class="container pt-6 pb-6 md:pb-12">
          <app-breadcrumb
            [links]="[
              {
                title: ('booking-page.breadcrumb-search' | transloco),
                routerLink: vendorLink(room.vendor.slug),
              },
              {
                title: room.name,
                routerLink: roomLink(room.vendor.slug, room.id),
              },
              {
                title: ('booking-page.breadcrumb-book' | transloco),
              },
            ]"
          ></app-breadcrumb>
          <app-public-booking
            #publicBookingComponent
            (bookingSaved)="bookingSaved($event)"
          ></app-public-booking>
        </div>
      }
    } @else if (roomLoading()) {
      <div class="container pt-6 pb-6 md:pb-12">
        <!-- Breadcrumb -->
        <app-skeleton-loader width="320px" height="16px"></app-skeleton-loader>

        <!-- Bloc formulaire de réservation -->
        <div class="mt-3 grid grid-cols-1 gap-8 lg:grid-cols-3 lg:gap-6">
          <!-- Formulaire principal -->
          <div class="space-y-6 lg:col-span-2">
            <app-skeleton-loader
              class="block"
              width="100%"
              height="500px"
            ></app-skeleton-loader>
          </div>

          <!-- Récapitulatif de réservation -->
          <div class="lg:col-span-1">
            <app-skeleton-loader
              class="block"
              width="100%"
              height="320px"
            ></app-skeleton-loader>
          </div>
        </div>
      </div>
    } @else if (roomLoadingError() || roomIsInactiveOrPrivateOrNotListed()) {
      <app-not-found></app-not-found>
    } @else if (authUserIsNotAllowedToCreatePublicBooking()) {
      <div class="container pt-10 text-center md:pt-40">
        <p class="text-3xl font-bold uppercase">{{ 'booking-page.logout-required-title' | transloco }}</p>
        <p class="mt-4 text-lg">
          {{ 'booking-page.unauthorized-intro' | transloco: { email: authUser()?.email } }}<br />
          {{ 'booking-page.unauthorized-action-prefix' | transloco }}
          <a
            class="underline underline-offset-4"
            (click)="logout(redirectUrl())"
          >{{ 'booking-page.unauthorized-logout-link' | transloco }}</a>.
        </p>
      </div>
    }
  `,
  standalone: false,
})
export class BookingPageComponent
  extends LogoutBaseComponent
  implements OnInit
{
  @ViewChild('publicBookingComponent')
  publicBookingComponent: PublicBookingComponent;
  // Champs de chargement affichés pendant le chargement de la salle
  roomsService = inject(RoomsService);
  private localizedUrlService = inject(LocalizedUrlService);

  // Liens localisés pour le fil d'Ariane
  vendorLink(vendorSlug: string): string[] {
    return this.localizedUrlService.link('vendor', { vendorSlug });
  }

  roomLink(vendorSlug: string, id: number): string[] {
    return this.localizedUrlService.link('room', { vendorSlug, id });
  }

  room$: Observable<Room> = this.store.select(fromStore.selectBookingFormRoom);
  room = this.store.selectSignal(fromStore.selectBookingFormRoom);
  roomLoadingError = this.store.selectSignal(selectRoomsError);
  roomLoading = this.store.selectSignal(selectRoomsLoading);
  roomIsInactiveOrPrivateOrNotListed = computed(() => {
    const room = this.room() as Room | null;
    const authUser = this.authUser();
    const isInternalUser = authUser?.vendors?.some(
      (v) => v.id === room?.vendor_id,
    );

    return (
      room?.bookable_by === BookableBy.Private ||
      !room?.active ||
      !this.roomsService.canUserAccessRoom(room) ||
      (!isInternalUser && !room?.is_bookable_publicly)
    );
  });
  authUser = this.store.selectSignal(selectAuthUser);
  isLoggedIn = this.store.selectSignal(fromStore.isLoggedIn);
  authUserIsNotAllowedToCreatePublicBooking = computed(() => {
    if (!this.isLoggedIn()) {
      return false;
    }

    return !this.authUser()?.all_permissions.includes(
      BookingPermission.StorePublic,
    );
  });
  // Hack fait rapidement pour fix prod -> le logout sans redirection affichait unauthorizedError() (room pas définie)
  redirectUrl = computed(() => {
    const vendor = this.store.selectSignal(fromStore.selectPublicVendor);
    if (!vendor()) {
      return '';
    }

    return this.localizedUrlService.link('vendor', {
      vendorSlug: vendor().slug,
    }).join('/');
  });

  constructor(
    private store: Store,
    private route: ActivatedRoute,
    public router: Router,
    private title: Title,
    authService: AuthService,
    toastService: ToastService,
    modalService: ModalService,
  ) {
    super(modalService, authService, toastService);
  }

  async ngOnInit() {
    if (this.route.snapshot.queryParams['payment_failed']) {
      this.toastService.error(
        this.translocoService.translate('booking-page.payment-failed'),
        null,
        null,
      );
    }
    const id = parseInt(this.route.snapshot.params['id'], 10);
    this.store.dispatch(roomsActions.needRoom({ id }));

    const currentBookingData = await firstValueFrom(
      this.store.select(selectBookingFormData).pipe(take(1)),
    );

    // Reset si la salle change OU si un id est présent (session d'édition précédente)
    if (currentBookingData.room_id !== id || currentBookingData.id) {
      this.store.dispatch(bookingFormActions.resetForm());
      this.store.dispatch(
        bookingFormActions.setData({
          room_id: id,
        }),
      );
    }

    this.setPageTitle();
  }

  setPageTitle() {
    combineLatest([
      this.room$.pipe(skipWhile((room) => !room)),
      this.store
        .select(fromStore.selectPublicVendor)
        .pipe(skipWhile((vendor) => !vendor)),
    ])
      .pipe(take(1))
      .subscribe(([room, vendor]) => {
        this.title.setTitle(
          this.translocoService.translate('booking-page.page-title', {
            room: room.name,
            vendor: vendor.name,
          }),
        );
      });
  }

  async bookingSaved(booking: Booking) {
    const user = await firstValueFrom(
      this.store.select(fromStore.selectAuthUser).pipe(take(1)),
    );
    const room = await firstValueFrom(this.room$.pipe(take(1)));
    if (user && user.vendor?.id === room.vendor_id) {
      this.toastService.success(
        this.translocoService.translate('booking-page.booking-saved', { name: booking.full_name }),
      );
      await this.router.navigateByUrl('/reservations?date=' + booking.start);
      this.store.dispatch(bookingFormActions.resetForm());
    } else {
      await this.router.navigate(
        this.localizedUrlService.link('bookingConfirmation', {
          vendorSlug: this.route.snapshot.params['vendorSlug'],
          id: this.route.snapshot.params['id'],
        }),
      );
    }
  }
}
